import {logDevToolsPrivacy} from '../../utils/AnalyticsUtils';
function DevToolsPrivacy() {
    logDevToolsPrivacy();
    return(
        <div>
             <h1>Privacy Policy</h1>
  <p><strong>aDevTools</strong> · Effective Date: April 8, 2026</p>
  <hr/>
  <p>
   Luis Genaro Arvizu Vega ("we", "us", or "our") built the aDevTools app as a Freemium application. This Privacy Policy explains how we collect, use, and disclose information when you use our Service. By using aDevTools, you agree to the practices described here.
  </p>
  <p>
   This policy applies globally. Depending on your location, additional rights may apply — see the Regional Rights section below.
  </p>
  <h2>
   1. Information We Collect
  </h2>
  <h3>
   1.1 Information You Provide
  </h3>
  <ul>
   <li>
    <strong>
     Account Information:
    </strong>
    If you choose to sign in (optional), we collect your name, email address, and authentication credentials via third-party sign-in providers such as Google or Apple.
   </li>
   <li>
    <strong>
     Profile Data:
    </strong>
    Any optional information you provide when setting up your account.
   </li>
  </ul>
  <h3>
   1.2 Information Collected Automatically
  </h3>
  <ul>
   <li>
    <strong>
     Usage Data:
    </strong>
    Firebase Analytics collects data about how you interact with the app, including features used, session duration, in-app events, and navigation patterns.
   </li>
   <li>
    <strong>
     Crash &amp; Diagnostic Data:
    </strong>
    Firebase Crashlytics collects crash reports, stack traces, device state at crash time, and related diagnostic information.
   </li>
   <li>
    <strong>
     Log Data:
    </strong>
    In the event of errors, we may collect your device's IP address, device name, operating system version, app configuration, timestamps, and other statistics.
   </li>
   <li>
    <strong>
     Advertising Data:
    </strong>
    Google AdMob may collect your advertising identifier (IDFA/GAID), IP address, and interaction data with ads to serve personalized or non-personalized ads.
   </li>
  </ul>
  <h3>
   1.3 Information from Third Parties
  </h3>
  <p>
   If you sign in using a third-party provider (e.g., Google, Apple), we receive basic profile information such as your name and email address as permitted by your settings with that provider.
  </p>
  <h2>
   2. How We Use Your Information
  </h2>
  <ul>
   <li>
    To provide, maintain, and improve the Service
   </li>
   <li>
    To personalize your experience and remember your preferences
   </li>
   <li>
    To serve advertisements through Google AdMob (which may be personalized based on your interests)
   </li>
   <li>
    To analyze app usage and performance via Firebase Analytics
   </li>
   <li>
    To diagnose crashes and bugs via Firebase Crashlytics
   </li>
   <li>
    To manage your account and authenticate your identity if you choose to sign in
   </li>
   <li>
    To process subscription payments and manage your subscription status
   </li>
   <li>
    To communicate with you about updates, changes, or support requests
   </li>
   <li>
    To comply with legal obligations
   </li>
  </ul>
  <h2>
   3. Subscriptions &amp; Billing
  </h2>
  <p>
   aDevTools offers optional paid subscriptions that unlock premium features. Subscription purchases are processed by Apple (App Store) or Google (Play Store). We do not directly collect or store your payment card information.
  </p>
  <ul>
   <li>
    Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
   </li>
   <li>
    You can manage or cancel your subscription in your device's account settings.
   </li>
   <li>
    Refunds are handled by Apple or Google in accordance with their respective policies.
   </li>
   <li>
    Billing history and receipts are available through your App Store or Play Store account.
   </li>
  </ul>
  <h2>
   4. Advertising
  </h2>
  <p>
   We use Google AdMob to display ads in the free version of the app. AdMob may collect and use data to show you personalized ads based on your interests. This may include your advertising identifier (IDFA/GAID), approximate location (IP-based), and in-app activity.
  </p>
  <p>
   You can opt out of personalized advertising by adjusting your device settings:
  </p>
  <strong>
   iOS
  </strong>
  Settings → Privacy &amp; Security → Tracking
  <strong>
   Android
  </strong>
  Settings → Google → Ads → Opt out of Ads Personalization
  <p>
   For more information, see the
   <a href="https://policies.google.com/privacy" target="_blank">
    Google AdMob Privacy Policy
   </a>
   .
  </p>
  <h2>
   5. Third-Party Services
  </h2>
  <table>
   <thead>
    <tr>
     <th>
      Service
     </th>
     <th>
      Purpose
     </th>
     <th>
      Privacy Policy
     </th>
    </tr>
   </thead>
   <tbody>
    <tr>
     <td>
      Firebase Analytics
     </td>
     <td>
      Usage analytics
     </td>
     <td>
      <a href="https://firebase.google.com/support/privacy" target="_blank">
       firebase.google.com/support/privacy
      </a>
     </td>
    </tr>
    <tr>
     <td>
      Firebase Crashlytics
     </td>
     <td>
      Crash reporting
     </td>
     <td>
      <a href="https://firebase.google.com/support/privacy" target="_blank">
       firebase.google.com/support/privacy
      </a>
     </td>
    </tr>
    <tr>
     <td>
      Google AdMob
     </td>
     <td>
      Advertising
     </td>
     <td>
      <a href="https://policies.google.com/privacy" target="_blank">
       policies.google.com/privacy
      </a>
     </td>
    </tr>
   </tbody>
  </table>
  <p>
   These providers have access to your personal information only as necessary to perform their services and are obligated not to disclose or use it for any other purpose.
  </p>
  <h2>
   6. Cookies &amp; Tracking Technologies
  </h2>
  <p>
   The app itself does not use cookies. However, our third-party providers (Firebase, AdMob) may use cookies, SDKs, and similar tracking technologies. You may manage these through your device settings.
  </p>
  <h2>
   7. Data Retention
  </h2>
  <p>
   We retain your data for as long as your account is active or as needed to provide the Service. Upon account deletion:
  </p>
  <ul>
   <li>
    Account data will be removed within 30 days.
   </li>
   <li>
    Anonymized analytics data may be retained longer for aggregate analysis.
   </li>
   <li>
    Crash logs may be retained for up to 90 days.
   </li>
  </ul>
  <h2>
   8. Data Sharing &amp; Disclosure
  </h2>
  <p>
   We do not sell your personal information. We may share your data only:
  </p>
  <ul>
   <li>
    With service providers listed in Section 5 who assist in operating our Service
   </li>
   <li>
    If required by law, court order, or government authority
   </li>
   <li>
    To protect the rights, property, or safety of our users or the public
   </li>
   <li>
    In connection with a merger, acquisition, or sale of assets (users will be notified)
   </li>
  </ul>
  <h2>
   9. Children's Privacy
  </h2>
  <p>
   aDevTools is not directed at children under the age of 13 (or 16 in certain jurisdictions under GDPR). We do not knowingly collect information from children. If you believe your child has provided us with personal information, contact us immediately at contact@luisarvizu.dev and we will promptly delete such data.
  </p>
  <h2>
   10. Security
  </h2>
  <p>
   We implement commercially reasonable security measures to protect your information. However, no method of transmission over the internet is 100% secure. We encourage you to use a strong password and keep your device secure.
  </p>
  <h2>
   11. Regional Privacy Rights
  </h2>
  <h3>
   European Users (GDPR)
  </h3>
  <p>
   If you are located in the EEA, UK, or Switzerland, you have the following rights:
  </p>
  <strong>
   Access
  </strong>
  Request a copy of your personal data
  <strong>
   Rectification
  </strong>
  Correct inaccurate or incomplete data
  <strong>
   Erasure
  </strong>
  Request deletion ("right to be forgotten")
  <strong>
   Restriction
  </strong>
  Limit how we process your data
  <strong>
   Portability
  </strong>
  Receive your data in a machine-readable format
  <strong>
   Object
  </strong>
  Object to processing based on legitimate interests
  <p>
   Our legal bases for processing include: performance of a contract (subscriptions), legitimate interests (analytics, security), consent (personalized advertising), and legal compliance.
  </p>
  <h3>
   California Users (CCPA/CPRA)
  </h3>
  <ul>
   <li>
    Right to know what personal information we collect and how it is used
   </li>
   <li>
    Right to delete personal information we hold about you
   </li>
   <li>
    Right to opt out of sale or sharing of personal information (we do not sell personal data)
   </li>
   <li>
    Right to non-discrimination for exercising your rights
   </li>
  </ul>
  <h3>
   Other Regions
  </h3>
  <p>
   We aim to honor data privacy rights for all users globally. Contact us at contact@luisarvizu.dev to exercise any data right.
  </p>
  <h2>
   12. Links to Other Sites
  </h2>
  <p>
   Our Service may contain links to external websites not operated by us. We have no control over these sites and strongly advise you to review their respective Privacy Policies.
  </p>
  <h2>
   13. Changes to This Privacy Policy
  </h2>
  <p>
   We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the updated policy on this page and, where appropriate, notifying you through the app or by email.
  </p>
  <h2>
   14. Contact Us
  </h2>
  <p>
   <strong>
    Email: contact@luisarvizu.dev
   </strong>
  </p>
  <p>
   <strong>
    Developer:
   </strong>
   Luis Genaro Arvizu Vega
  </p>
  {/* <!-- ══════════════════════════════════════════════════ -->
  <!--  TERMS & CONDITIONS                                -->
  <!-- ══════════════════════════════════════════════════ --> */}
  <h1>Terms &amp; Conditions</h1>
  <p><strong>aDevTools</strong> · Effective Date: April 8, 2026</p>
  <hr/>
  <p>
   Please read these Terms and Conditions carefully before using aDevTools ("the App") provided by Luis Genaro Arvizu Vega ("we", "us", or "our"). By downloading or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.
  </p>
  <h2>
   1. Acceptance of Terms
  </h2>
  <p>
   By accessing or using aDevTools, you confirm that you are at least 13 years of age (or 16 in certain jurisdictions), that you have the legal capacity to enter into this agreement, and that you agree to comply with these Terms and Conditions.
  </p>
  <h2>
   2. License to Use the App
  </h2>
  <p>
   Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to use aDevTools for personal, non-commercial purposes. This license does not include the right to:
  </p>
  <ul>
   <li>
    Copy, modify, or distribute the App or any part of it
   </li>
   <li>
    Attempt to extract or reverse-engineer the source code
   </li>
   <li>
    Translate the App into other languages or create derivative works
   </li>
   <li>
    Use the App's trademarks or branding without prior written permission
   </li>
  </ul>
  <p>
   All intellectual property rights in the App remain the property of Luis Genaro Arvizu Vega.
  </p>
  <h2>
   3. User Accounts
  </h2>
  <p>
   aDevTools offers an optional sign-in feature. If you choose to create an account:
  </p>
  <ul>
   <li>
    You are responsible for maintaining the confidentiality of your credentials.
   </li>
   <li>
    You must provide accurate and current information when registering.
   </li>
   <li>
    You are responsible for all activity that occurs under your account.
   </li>
   <li>
    You must notify us immediately of any unauthorized use at contact@luisarvizu.dev .
   </li>
   <li>
    We reserve the right to suspend or terminate accounts that violate these Terms.
   </li>
  </ul>
  <p>
   You may delete your account at any time through the App's settings. Account data will be removed per our Privacy Policy.
  </p>
  <h2>
   4. Subscriptions
  </h2>
  <h3>
   4.1 Subscription Plans
  </h3>
  <p>
   aDevTools offers free (ad-supported) and premium (subscription-based) tiers. Premium subscriptions unlock additional features and remove advertisements.
  </p>
  <h3>
   4.2 Billing &amp; Renewal
  </h3>
  <ul>
   <li>
    Subscriptions are billed on a recurring basis (monthly or annually, as selected).
   </li>
   <li>
    Payment is charged to your App Store or Google Play account at confirmation of purchase.
   </li>
   <li>
    Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current billing period.
   </li>
   <li>
    Your account will be charged for renewal within 24 hours prior to the end of the current period.
   </li>
  </ul>
  <h3>
   4.3 Cancellation
  </h3>
  <ul>
   <li>
    You may cancel your subscription at any time through your App Store (iOS) or Google Play (Android) account settings.
   </li>
   <li>
    Cancellation takes effect at the end of the current billing period; you retain access to premium features until that date.
   </li>
   <li>
    We do not provide refunds for partially used subscription periods except where required by applicable law.
   </li>
  </ul>
  <h3>
   4.4 Price Changes
  </h3>
  <p>
   We reserve the right to change subscription prices. We will provide reasonable advance notice of any price changes. Continued use of the subscription after the price change constitutes acceptance.
  </p>
  <h3>
   4.5 Free Trials
  </h3>
  <p>
   If we offer a free trial, it will convert automatically to a paid subscription at the end of the trial period unless you cancel before the trial ends.
  </p>
  <h2>
   5. Advertising
  </h2>
  <p>
   The free version of aDevTools displays advertisements served by Google AdMob. By using the free version, you agree to receive ads. Ads may be personalized based on your interests as described in our Privacy Policy. You can remove ads by upgrading to a premium subscription.
  </p>
  <h2>
   6. Acceptable Use
  </h2>
  <p>
   You agree not to use the App to:
  </p>
  <ul>
   <li>
    Violate any applicable laws or regulations
   </li>
   <li>
    Infringe on the intellectual property rights of others
   </li>
   <li>
    Transmit harmful, offensive, or unlawful content
   </li>
   <li>
    Attempt to gain unauthorized access to our systems or other users' accounts
   </li>
   <li>
    Interfere with or disrupt the integrity or performance of the Service
   </li>
   <li>
    Use automated tools (bots, scrapers) to access the App
   </li>
  </ul>
  <h2>
   7. Third-Party Services
  </h2>
  <p>
   The App integrates the following third-party services, each governed by their own Terms and Conditions:
  </p>
  <ul>
   <li>
    <strong>
     Firebase Analytics
    </strong>
    — usage analytics
   </li>
   <li>
    <strong>
     Firebase Crashlytics
    </strong>
    — crash reporting
   </li>
   <li>
    <strong>
     Google AdMob
    </strong>
    — advertising
   </li>
   <li>
    <strong>
     Apple Sign In / Google Sign In
    </strong>
    — optional authentication
   </li>
  </ul>
  <p>
   We are not responsible for the practices of these third parties.
  </p>
  <h2>
   8. Device &amp; Connectivity
  </h2>
  <ul>
   <li>
    Certain features require an active internet connection. We are not responsible for the App not functioning if you lack connectivity.
   </li>
   <li>
    Mobile data roaming charges may apply if you use the App outside your home territory. You accept responsibility for all such charges.
   </li>
   <li>
    We recommend you do not jailbreak or root your device, as doing so may compromise the App's security and functionality.
   </li>
   <li>
    You are responsible for keeping your device charged and operational.
   </li>
  </ul>
  <h2>
   9. Updates &amp; Availability
  </h2>
  <p>
   We reserve the right to modify, update, or discontinue the App at any time without prior notice. We may stop providing the App and terminate your access at any time. Upon termination, all licenses granted under these Terms will end and you must cease using the App.
  </p>
  <h2>
   10. Disclaimer of Warranties
  </h2>
  <p>
   The App is provided "as is" and "as available" without warranties of any kind, express or implied. To the fullest extent permitted by law, we disclaim all warranties including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the App will be error-free or uninterrupted.
  </p>
  <h2>
   11. Limitation of Liability
  </h2>
  <p>
   To the maximum extent permitted by applicable law, Luis Genaro Arvizu Vega shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the App. Our total liability for any claims shall not exceed the amount you paid us in the 12 months preceding the claim.
  </p>
  <h2>
   12. Indemnification
  </h2>
  <p>
   You agree to indemnify, defend, and hold harmless Luis Genaro Arvizu Vega from and against any claims, liabilities, damages, losses, and expenses arising from: (a) your use of the App; (b) your violation of these Terms; or (c) your violation of any third-party rights.
  </p>
  <h2>
   13. Governing Law
  </h2>
  <p>
   These Terms are governed by the laws of Mexico, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration or in the courts of competent jurisdiction in Mexico.
  </p>
  <h2>
   14. Changes to These Terms
  </h2>
  <p>
   We may update these Terms from time to time. We will notify you of material changes by posting the updated Terms in the App and updating the effective date above. Continued use of the App after changes are posted constitutes acceptance.
  </p>
        </div>
    );
}

export default DevToolsPrivacy;